import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useCompany } from "@/lib/company";
import { acct, fmtDate, kes, today } from "@/lib/money";
import { Page, Loading } from "@/components/kit";

const tile = "rounded-[10px] border border-[#E4E2DE] bg-white p-5";
const big = "mt-2 font-mono text-[26px] font-semibold tabular-nums";

function Rows({ rows }) {
  return (
    <div className="mt-3 flex flex-col text-sm">
      {rows.map((r) => (
        <Link key={r.account_id} to={`/ledger/${r.account_id}`} className="flex justify-between gap-3 border-b border-[#EEECE8] py-2 last:border-0 hover:text-brand">
          <span>{r.name}</span><span className="font-mono tabular-nums">{acct(r.balance)}</span>
        </Link>
      ))}
      {rows.length === 0 && <span className="py-2 text-[#5F5C58]">Nothing yet</span>}
    </div>
  );
}

export default function Dashboard() {
  const { company } = useCompany();
  const [bs, setBs] = useState(null);
  const [pl, setPl] = useState(null);
  const [recent, setRecent] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const started = company && today() >= company.books_start;
  useEffect(() => {
    if (!company || today() < company.books_start) return;
    const cid = company.id;
    api.get(`/companies/${cid}/reports/balance-sheet`, { params: { as_of: today() } }).then(({ data }) => setBs(data));
    api.get(`/companies/${cid}/reports/profit-loss`, { params: { date_from: company.books_start, date_to: today() } }).then(({ data }) => setPl(data));
    api.get(`/companies/${cid}/journals`).then(({ data }) => setRecent(data.slice(0, 6)));
    api.get(`/companies/${cid}/accounts`).then(({ data }) => setAccounts(data));
  }, [company]);
  if (company && !started) {
    return (
      <Page title="Accounts dashboard">
        <section className={`${tile} mt-4 max-w-2xl`}>
          <h2 className="text-[15px] font-semibold">The books start on {fmtDate(company.books_start)}</h2>
          <p className="mt-2 text-sm text-[#5F5C58]">Until then the trial run uses the operations side (trips, fuel, requests and payments).
            The opening balances as at the day before go in as the first journal - see Company setup.</p>
          <Link to="/accounts" className="mt-3 inline-block text-sm font-semibold text-brand hover:underline">See the chart of accounts</Link>
        </section>
      </Page>
    );
  }
  if (!company || !bs || !pl) return <Page title="Accounts dashboard"><Loading /></Page>;
  const assets = [...bs.groups.current_assets, ...bs.groups.non_current_assets];
  const pick = (subs) => assets.filter((r) => subs.includes(r.subtype));
  // every cash and bank account, a new one at nil too (e.g. a bank with nothing posted yet)
  const cash = [...pick(["cash_bank"]), ...accounts.filter((a) => a.subtype === "cash_bank" && a.active !== false && !assets.some((r) => r.account_id === a.id))
    .map((a) => ({ account_id: a.id, code: a.code, name: a.name, balance: 0 }))].sort((a, b) => String(a.code).localeCompare(String(b.code)));
  const stock = pick(["stock"]);
  const owedTo = pick(["receivable"]);
  const owes = [...bs.groups.current_liabilities, ...bs.groups.long_term_liabilities].filter((r) => r.subtype !== "tax");
  const vatIn = assets.filter((r) => r.subtype === "tax").reduce((a, r) => a + r.balance, 0);
  const vatOut = bs.groups.current_liabilities.filter((r) => r.subtype === "tax").reduce((a, r) => a + r.balance, 0);
  const sum = (rows) => rows.reduce((a, r) => a + r.balance, 0);
  return (
    <Page title="Accounts dashboard">
      <div className="mt-1 text-sm text-[#5F5C58]">{fmtDate(company.books_start)} – {fmtDate(today())}</div>
      <div className="mt-5 grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))" }}>
        <section className={tile}>
          <h2 className="text-[15px] font-semibold">Profit &amp; loss</h2>
          <div className={`${big} ${pl.net_profit < 0 ? "text-[#A11D1D]" : "text-[#1F6B3A]"}`}>{acct(pl.net_profit)}</div>
          <div className="mt-3 flex flex-col text-sm">
            <div className="flex justify-between border-b border-[#EEECE8] py-2"><span>Income</span><span className="font-mono tabular-nums">{kes(pl.revenue)}</span></div>
            <div className="flex justify-between border-b border-[#EEECE8] py-2"><span>Gross profit</span><span className="font-mono tabular-nums">{acct(pl.gross_profit)}</span></div>
            <div className="flex justify-between py-2"><span>Expenses</span><span className="font-mono tabular-nums">{kes(pl.totals.operating)}</span></div>
          </div>
          <Link to="/reports?tab=pl" className="mt-2 inline-block text-sm font-semibold text-brand hover:underline">View report</Link>
        </section>
        <section className={tile}><h2 className="text-[15px] font-semibold">Cash &amp; bank</h2><div className={big}>{kes(sum(cash))}</div><Rows rows={cash} /></section>
        <section className={tile}><h2 className="text-[15px] font-semibold">Stock (fuel depot, spares, tyres)</h2><div className={big}>{kes(sum(stock))}</div><Rows rows={stock} /></section>
        <section className={tile}><h2 className="text-[15px] font-semibold">Owed to {company.short}</h2><div className={big}>{kes(sum(owedTo))}</div><Rows rows={owedTo} /></section>
        <section className={tile}><h2 className="text-[15px] font-semibold">{company.short} owes</h2><div className={big}>{kes(sum(owes))}</div><Rows rows={owes} /></section>
        <section className={tile}>
          <h2 className="text-[15px] font-semibold">VAT</h2>
          <div className={`${big} ${vatIn - vatOut >= 0 ? "text-[#1F6B3A]" : ""}`}>{kes(Math.abs(vatIn - vatOut))}</div>
          <div className="text-sm text-[#5F5C58]">{vatIn - vatOut >= 0 ? "Recoverable" : "Payable"} · input {kes(vatIn)} · output {kes(vatOut)}</div>
        </section>
      </div>
      <section className={`${tile} mt-4`}>
        <div className="flex items-baseline justify-between"><h2 className="text-[15px] font-semibold">Latest journals</h2><Link to="/journals" className="text-sm font-semibold text-brand hover:underline">All journals</Link></div>
        <div className="mt-2 flex flex-col text-sm">
          {recent.map((j) => (
            <div key={j.id} className="flex flex-wrap justify-between gap-3 border-b border-[#EEECE8] py-2 last:border-0">
              <span><span className="text-[#5F5C58]">{fmtDate(j.date)}</span> · {j.memo}</span><span className="font-mono tabular-nums">{kes(j.total)}</span>
            </div>
          ))}
          {recent.length === 0 && <span className="py-2 text-[#5F5C58]">No journals yet</span>}
        </div>
      </section>
    </Page>
  );
}
